/**
 * Wizard Progress Component
 *
 * Shows progress through the campaign creation wizard steps.
 */

import React from 'react';
import { Box, Typography, LinearProgress, Chip, Stepper, Step, StepLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useWizardStore from '../wizard.store';
import { CAMPAIGN_WIZARD_STEPS, CampaignWizardStep, WizardStepStatus } from '../wizard.types';

interface WizardProgressProps {
  compact?: boolean;
  showStepper?: boolean;
}

const WizardProgress: React.FC<WizardProgressProps> = ({ compact = false, showStepper = false }) => {
  const { classes, cx } = useStyles({ compact });
  const { t } = useTranslation();
  const { activeWizard, getProgress, goToStep } = useWizardStore();

  if (!activeWizard) return null;

  const progress = getProgress();
  const visibleSteps = CAMPAIGN_WIZARD_STEPS.filter(s => s.id !== 'complete');

  const getStepIcon = (status: WizardStepStatus, isActive: boolean) => {
    if (status === 'confirmed') return <CheckCircleIcon className={cx(classes.stepIcon, 'confirmed')} />;
    if (status === 'error') return <ErrorIcon className={cx(classes.stepIcon, 'error')} />;
    if (isActive) return <EditIcon className={cx(classes.stepIcon, 'active')} />;
    return <RadioButtonUncheckedIcon className={cx(classes.stepIcon, 'pending')} />;
  };

  const handleStepClick = (step: CampaignWizardStep) => {
    const stepState = activeWizard.steps[step];
    // Can click on confirmed steps or current step
    if (stepState.status === 'confirmed' || step === activeWizard.currentStep) {
      goToStep(step);
    }
  };

  if (compact) {
    return (
      <Box className={classes.compactContainer}>
        <Box className={classes.compactHeader}>
          <Typography variant="caption" className={classes.progressLabel}>
            {t('step', 'Step')} {progress.current + 1} / {progress.total}
          </Typography>
          <Chip
            size="small"
            label={`${progress.percentage}%`}
            color={progress.percentage === 100 ? 'success' : 'primary'}
            className={classes.progressChip}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress.percentage}
          className={classes.progressBar}
        />
        <Typography variant="body2" className={classes.currentStepName}>
          {t(activeWizard.currentStep, { defaultValue: CAMPAIGN_WIZARD_STEPS.find(s => s.id === activeWizard.currentStep)?.name || '' })}
        </Typography>
      </Box>
    );
  }

  if (showStepper) {
    return (
      <Stepper
        activeStep={visibleSteps.findIndex(s => s.id === activeWizard.currentStep)}
        className={classes.stepper}
        alternativeLabel
      >
        {visibleSteps.map((step) => {
          const stepState = activeWizard.steps[step.id];
          const isActive = step.id === activeWizard.currentStep;

          return (
            <Step
              key={step.id}
              completed={stepState.status === 'confirmed'}
              onClick={() => handleStepClick(step.id)}
              className={cx(classes.step, stepState.status === 'confirmed' && 'clickable')}
            >
              <StepLabel
                StepIconComponent={() => getStepIcon(stepState.status, isActive)}
                className={classes.stepLabel}
              >
                {t(step.id, step.name)}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="subtitle2" className={classes.title}>
          {t('campaignWizard', 'Campaign Wizard')}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {progress.current} / {progress.total} {t('stepsCompleted', 'steps completed')}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress.percentage}
        className={classes.progressBar}
      />

      <Box className={classes.stepsList}>
        {visibleSteps.map((step) => {
          const stepState = activeWizard.steps[step.id];
          const isActive = step.id === activeWizard.currentStep;
          const isClickable = stepState.status === 'confirmed' || isActive;

          return (
            <Box
              key={step.id}
              className={cx(
                classes.stepItem,
                isActive && 'active',
                stepState.status === 'confirmed' && 'confirmed',
                isClickable && 'clickable'
              )}
              onClick={() => isClickable && handleStepClick(step.id)}
            >
              {getStepIcon(stepState.status, isActive)}
              <Box className={classes.stepContent}>
                <Typography variant="body2" className={classes.stepName}>
                  {t(step.id, step.name)}
                </Typography>
                {isActive && (
                  <Typography variant="caption" color="textSecondary">
                    {t(step.id + '_desc', step.description)}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = makeStyles<{ compact?: boolean }>()((theme, { compact }) => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 12,
  },
  compactContainer: {
    padding: theme.spacing(1.5),
  },
  compactHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  title: {
    fontWeight: 600,
  },
  progressLabel: {
    color: theme.palette.text.secondary,
  },
  progressChip: {
    height: 20,
    fontSize: '0.65rem',
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      borderRadius: 3,
    },
  },
  currentStepName: {
    fontWeight: 500,
  },
  stepper: {
    padding: theme.spacing(2, 0),
  },
  step: {
    cursor: 'default',
    '&.clickable': {
      cursor: 'pointer',
    },
  },
  stepLabel: {
    '& .MuiStepLabel-label': {
      fontSize: '0.75rem',
    },
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    cursor: 'default',
    transition: 'background-color 0.2s',
    '&.active': {
      backgroundColor: theme.palette.primary.main + '10',
    },
    '&.clickable': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    '&.confirmed': {
      opacity: 0.8,
    },
  },
  stepContent: {
    flex: 1,
    minWidth: 0,
  },
  stepName: {
    fontWeight: 500,
    lineHeight: 1.3,
  },
  stepIcon: {
    fontSize: '1.25rem',
    marginTop: 2,
    '&.confirmed': {
      color: theme.palette.success.main,
    },
    '&.active': {
      color: theme.palette.primary.main,
    },
    '&.pending': {
      color: theme.palette.grey[400],
    },
    '&.error': {
      color: theme.palette.error.main,
    },
  },
}));

export default WizardProgress;

